import { Checkbox, Table, Typography } from 'antd';
import { FC } from 'react';

import type { AddressPlan, AddressValues } from '@hooks/api';

import * as S from './styled';

const { Text } = Typography;

/** The labels the company form and the invoicing check use for the same five parts. */
const FIELDS: { key: keyof AddressValues; label: string }[] = [
  { key: 'houseNumber', label: 'House number' },
  { key: 'street', label: 'Street' },
  { key: 'postalCode', label: 'Postal code' },
  { key: 'city', label: 'City' },
  { key: 'countryCode', label: 'Country' },
];

const LABEL = Object.fromEntries(
  FIELDS.map(({ key, label }) => [key, label.toLowerCase()])
) as Record<keyof AddressValues, string>;

const SUMMARY: Record<AddressPlan['action'], string> = {
  overwrite:
    "Master Data's registered office replaces the address legacy holds, all five parts at once.",
  unchanged: 'Legacy already holds what Master Data would write.',
  skipped: '',
};

type Row = {
  key: string;
  label: string;
  current: string | null;
  proposed: string | null;
  after: string | null;
};

const value = (text: string | null) => text ?? <Text type="secondary">—</Text>;

type Props = {
  plan: AddressPlan;
  overwrite: boolean;
  onOverwriteChange: (overwrite: boolean) => void;
};

/**
 * The company address the action writes, shown before it is pressed.
 *
 * The same shape as the contact person's section, for the same reason: the run replaces what legacy
 * holds — a hand edit included — so both sides and the outcome are on screen, with a switch. One
 * difference is worth the extra line: a part Master Data does not hold is *emptied*, not kept, and
 * invoicing refuses a company missing any of the five, so the section names those parts.
 */
const AddressSection: FC<Props> = ({ plan, overwrite, onOverwriteChange }) => {
  const writes = plan.action === 'overwrite';
  const after = writes && overwrite ? plan.result : plan.current;

  const rows: Row[] = FIELDS.map(({ key, label }) => ({
    key,
    label,
    current: plan.current[key],
    proposed: plan.proposed?.[key] ?? null,
    after: after[key],
  }));

  const missing = plan.missing.map((part) => LABEL[part]);

  return (
    <div>
      <S.SectionLabel>Address</S.SectionLabel>
      <S.SectionNote type="secondary">
        {plan.action === 'skipped'
          ? plan.reason
          : writes && !overwrite
            ? 'The overwrite is switched off for this run, so the address stays as legacy holds it.'
            : SUMMARY[plan.action]}
        {writes && overwrite && missing.length > 0 && (
          <>
            {' '}
            Master Data has no {missing.join(', ')}, so legacy will have none
            either — invoicing will ask for{' '}
            {missing.length === 1 ? 'it' : 'them'}.
          </>
        )}
      </S.SectionNote>
      <Table<Row>
        size="small"
        rowKey="key"
        pagination={false}
        dataSource={rows}
        columns={[
          { title: '', key: 'label', dataIndex: 'label', width: 110 },
          {
            title: 'In legacy now',
            key: 'current',
            render: (_: unknown, row) => value(row.current),
          },
          {
            title: 'From Master Data',
            key: 'proposed',
            render: (_: unknown, row) => value(row.proposed),
          },
          {
            title: 'After this run',
            key: 'after',
            render: (_: unknown, row) =>
              row.after !== row.current ? (
                <Text strong>{row.after || '—'}</Text>
              ) : (
                value(row.after)
              ),
          },
        ]}
      />
      {/* Offered whatever the preview says, as for the contact person: the plan is recomputed at the press. */}
      <Checkbox
        checked={overwrite}
        onChange={(event) => onOverwriteChange(event.target.checked)}
      >
        Overwrite the address from Master Data
      </Checkbox>
    </div>
  );
};

export default AddressSection;
